import React, { useEffect, useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import Chip from '@mui/material/Chip';
import { CronogramaAmortizacao, CronogramaTipo } from '../../../types/ativoMaster';
import { getCronogramasByAtivoMaster, getAtivoMaster } from '../../../services/ativoMasterService';
import { getTipoColor } from '../../../utils/cronogramaColors';

interface Props {
  ativoMasterId: number;
}

// ─── Tipos de evento consolidado ─────────────────────────────────────────────

interface EventoTipo {
  tipo: CronogramaTipo;
  tipoLabel: string;
}

interface EventoAgrupado {
  seq: number;
  data: string;
  dataObj: Date;
  tipos: EventoTipo[];
}

const parseData = (s: string): Date => {
  const [d, m, y] = s.split('/');
  return new Date(parseInt(y, 10), parseInt(m, 10) - 1, parseInt(d, 10));
};

const thSx = {
  backgroundColor: '#F4F7F9',
  color: '#4D5766',
  fontWeight: 600,
  fontFamily: 'Inter, sans-serif',
  fontSize: '12px',
  borderBottom: '2px solid #CFD8E5',
  whiteSpace: 'nowrap' as const,
};

// ─── Componente ───────────────────────────────────────────────────────────────

const CronogramaAtivoSection: React.FC<Props> = ({ ativoMasterId }) => {
  const [cronogramas,  setCronogramas]  = useState<CronogramaAmortizacao[]>([]);
  const [descricao,    setDescricao]    = useState<string | null>(null);
  const [loading, setLoading]           = useState(true);
  const [error, setError]               = useState<string | null>(null);
  const [filtroTipo, setFiltroTipo]     = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    setError(null);
    setFiltroTipo(null);
    Promise.all([
      getCronogramasByAtivoMaster(ativoMasterId),
      getAtivoMaster(ativoMasterId),
    ])
      .then(([crons, master]) => {
        setCronogramas(crons);
        setDescricao(master.cronograma_observacoes ?? null);
      })
      .catch(() => setError('Erro ao carregar cronograma.'))
      .finally(() => setLoading(false));
  }, [ativoMasterId]);

  const hoje = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);

  // Agrupa eventos por data em ordem cronológica
  const todosEventos = useMemo<EventoAgrupado[]>(() => {
    const byDate: Record<string, EventoAgrupado> = {};
    cronogramas.forEach(cron => {
      const label = cron.tipo === 'PERSONALIZADO' && cron.descricao
        ? cron.descricao
        : cron.tipo_display;
      cron.datas_geradas.forEach(d => {
        if (!byDate[d.data]) {
          byDate[d.data] = { seq: 0, data: d.data, dataObj: parseData(d.data), tipos: [] };
        }
        byDate[d.data].tipos.push({ tipo: cron.tipo, tipoLabel: label });
      });
    });
    const lista = Object.values(byDate).sort((a, b) => a.dataObj.getTime() - b.dataObj.getTime());
    lista.forEach((e, i) => { e.seq = i + 1; });
    return lista;
  }, [cronogramas]);

  // Tipos únicos presentes — para os chips de filtro
  const tiposPresentes = useMemo<CronogramaTipo[]>(() => {
    const seen = new Set<CronogramaTipo>();
    cronogramas.forEach(c => seen.add(c.tipo));
    return Array.from(seen);
  }, [cronogramas]);

  // Filtro: mantém só datas com o tipo selecionado, exibe só esse badge
  const eventosFiltrados = useMemo<EventoAgrupado[]>(() => {
    if (!filtroTipo) return todosEventos;
    return todosEventos
      .filter(e => e.tipos.some(t => t.tipo === filtroTipo))
      .map(e => ({ ...e, tipos: e.tipos.filter(t => t.tipo === filtroTipo) }));
  }, [todosEventos, filtroTipo]);

  // Índice do próximo evento futuro na lista filtrada
  const proximaIdx = useMemo(() =>
    eventosFiltrados.findIndex(e => e.dataObj >= hoje),
    [eventosFiltrados, hoje],
  );

  if (loading) return (
    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
      <CircularProgress size={24} />
    </Box>
  );

  if (error) return (
    <Typography sx={{ py: 3, textAlign: 'center', color: '#E51739', fontSize: '13px', fontFamily: 'Inter' }}>
      {error}
    </Typography>
  );

  if (cronogramas.length === 0) return (
    <Typography sx={{ py: 3, textAlign: 'center', color: '#8A98A8', fontSize: '13px', fontFamily: 'Inter' }}>
      Nenhum cronograma de amortização cadastrado.
    </Typography>
  );

  return (
    <Box sx={{ p: 2 }}>

      {/* Descrição geral do cronograma */}
      {descricao && (
        <Typography sx={{
          fontSize: '13px',
          fontFamily: 'Inter, sans-serif',
          color: '#4D5766',
          mb: 1.5,
          px: 1.5,
          py: 1,
          backgroundColor: '#F4F7F9',
          borderRadius: 1,
          borderLeft: '3px solid #90CAF9',
        }}>
          {descricao}
        </Typography>
      )}

      {/* Chips de filtro */}
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1.5 }}>
        <Chip
          label="Todos"
          size="small"
          onClick={() => setFiltroTipo(null)}
          sx={{
            fontSize: '11px', fontFamily: 'Inter', fontWeight: filtroTipo === null ? 700 : 400,
            backgroundColor: filtroTipo === null ? '#1A2332' : '#F4F7F9',
            color: filtroTipo === null ? '#fff' : '#4D5766',
            border: '1px solid',
            borderColor: filtroTipo === null ? '#1A2332' : '#CFD8E5',
            '&:hover': { backgroundColor: filtroTipo === null ? '#1A2332' : '#E8EDF2' },
          }}
        />
        {tiposPresentes.map(tipo => {
          const cron   = cronogramas.find(c => c.tipo === tipo)!;
          const label  = tipo === 'PERSONALIZADO' && cron.descricao ? cron.descricao : cron.tipo_display;
          const style  = getTipoColor(tipo);
          const active = filtroTipo === tipo;
          return (
            <Chip
              key={tipo}
              label={label}
              size="small"
              onClick={() => setFiltroTipo(active ? null : tipo)}
              sx={{
                fontSize: '11px', fontFamily: 'Inter', fontWeight: active ? 700 : 400,
                backgroundColor: active ? style.color : style.bg,
                color: active ? '#fff' : style.color,
                border: `1px solid ${style.border}`,
                '&:hover': { backgroundColor: active ? style.color : style.border },
              }}
            />
          );
        })}
      </Box>

      {/* Tabela consolidada */}
      <TableContainer
        component={Paper}
        elevation={0}
        sx={{ border: '1px solid #CFD8E5', borderRadius: 1.5, maxHeight: 340, overflowY: 'auto' }}
      >
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={{ ...thSx, width: 40, textAlign: 'center' }}>#</TableCell>
              <TableCell sx={thSx}>Data</TableCell>
              <TableCell sx={thSx}>Tipo</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {eventosFiltrados.length === 0 ? (
              <TableRow>
                <TableCell colSpan={3} align="center" sx={{ py: 3, color: '#8A98A8', fontSize: '12px', fontFamily: 'Inter' }}>
                  Nenhum evento encontrado.
                </TableCell>
              </TableRow>
            ) : (
              eventosFiltrados.map((evento, idx) => {
                const isPast    = evento.dataObj < hoje;
                const isProxima = idx === proximaIdx;
                return (
                  <TableRow
                    key={`${evento.data}-${evento.seq}`}
                    sx={{
                      backgroundColor: isProxima ? '#FFF8E7' : undefined,
                      opacity: isPast ? 0.4 : 1,
                      '&:nth-of-type(even)': {
                        backgroundColor: isProxima ? '#FFF8E7' : isPast ? undefined : '#FAFBFC',
                      },
                    }}
                  >
                    <TableCell sx={{ textAlign: 'center', fontSize: '12px', fontFamily: 'Inter', color: '#8A98A8' }}>
                      {evento.seq}
                    </TableCell>
                    <TableCell sx={{ fontSize: '12px', fontFamily: 'Inter', fontWeight: isProxima ? 700 : 400 }}>
                      {evento.data}
                      {isProxima && (
                        <Typography component="span" sx={{ fontSize: '10px', color: '#F5A623', fontFamily: 'Inter', ml: 0.75 }}>
                          próxima
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell sx={{ fontSize: '12px', fontFamily: 'Inter' }}>
                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                        {evento.tipos.map(t => {
                          const style = getTipoColor(t.tipo);
                          return (
                            <Box key={t.tipo} sx={{
                              display: 'inline-flex', alignItems: 'center',
                              px: 1, py: 0.15, borderRadius: 1,
                              backgroundColor: style.bg,
                              border: `1px solid ${style.border}`,
                            }}>
                              <Typography sx={{ fontSize: '11px', fontWeight: 600, fontFamily: 'Inter', color: style.color, whiteSpace: 'nowrap' }}>
                                {t.tipoLabel}
                              </Typography>
                            </Box>
                          );
                        })}
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Contagem */}
      <Typography sx={{ fontSize: '11px', color: '#8A98A8', fontFamily: 'Inter', mt: 0.75, textAlign: 'right' }}>
        {eventosFiltrados.length} evento{eventosFiltrados.length !== 1 ? 's' : ''}
        {filtroTipo ? ` · filtrado` : ` · ${todosEventos.filter(e => e.dataObj >= hoje).length} futuros`}
      </Typography>
    </Box>
  );
};

export default CronogramaAtivoSection;